/**
 * ADMIN MODULE — email sending config (non-secret) + test send.
 *
 * email_settings holds the safe-to-edit sender identity + master switch (the
 * Brevo API key and internal secret live in Supabase function secrets, never
 * here). RLS pins the table to platform admins. The test send invokes the
 * send-email edge function with the admin's own JWT (staff test-send path).
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SETTINGS_DEFAULTS = {
  from_email: "noreply@brandcommand.app",
  from_name: "Brand Command",
  reply_to: "",
  enabled: false,
  // Nurture sequence sender — independent of the notification sender above.
  nurture_from_email: "info@brandcommand.app",
  nurture_from_name: "Brand Command",
  nurture_reply_to: "",
};

export async function loadEmailSettings() {
  const { data, error } = await supabase
    .from("email_settings")
    .select("id, provider, from_email, from_name, reply_to, enabled, nurture_from_email, nurture_from_name, nurture_reply_to, updated_at")
    .eq("provider", "brevo")
    // `.limit(1)` is belt-and-braces on top of the unique index added in
    // migration 0068. `.maybeSingle()` THROWS on multi-row, so without a
    // cardinality guarantee a second brevo row would take this screen down —
    // the same failure mode as backlog item 34.
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error("[emailSettingsService.loadEmailSettings]", error);
    throw new Error("Could not load email settings.");
  }
  const row = data || {};
  return {
    from_email: row.from_email ?? SETTINGS_DEFAULTS.from_email,
    from_name: row.from_name ?? SETTINGS_DEFAULTS.from_name,
    reply_to: row.reply_to ?? "",
    enabled: !!row.enabled,
    nurture_from_email: row.nurture_from_email ?? SETTINGS_DEFAULTS.nurture_from_email,
    nurture_from_name: row.nurture_from_name ?? SETTINGS_DEFAULTS.nurture_from_name,
    nurture_reply_to: row.nurture_reply_to ?? "",
    updated_at: row.updated_at ?? null,
  };
}

const ALLOWED = new Set([
  "from_email", "from_name", "reply_to", "enabled",
  "nurture_from_email", "nurture_from_name", "nurture_reply_to",
]);

export async function saveEmailSettings(patch) {
  const out = {};
  for (const k of ALLOWED) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  if ("from_email" in out && !EMAIL_RE.test(out.from_email || "")) {
    throw new Error("Enter a valid From address.");
  }
  if ("nurture_from_email" in out && !EMAIL_RE.test(out.nurture_from_email || "")) {
    throw new Error("Enter a valid nurture From address.");
  }
  if ("reply_to" in out && out.reply_to && !EMAIL_RE.test(out.reply_to)) {
    throw new Error("Enter a valid reply-to address.");
  }
  if ("nurture_reply_to" in out && out.nurture_reply_to && !EMAIL_RE.test(out.nurture_reply_to)) {
    throw new Error("Enter a valid nurture reply-to address.");
  }
  if ("from_name" in out) out.from_name = String(out.from_name || "").slice(0, 120);
  if ("nurture_from_name" in out) out.nurture_from_name = String(out.nurture_from_name || "").slice(0, 120);
  if ("reply_to" in out && out.reply_to === "") out.reply_to = null;
  if ("nurture_reply_to" in out && out.nurture_reply_to === "") out.nurture_reply_to = null;
  if ("enabled" in out) out.enabled = !!out.enabled;

  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from("email_settings").update(out).eq("provider", "brevo");
  if (error) {
    console.error("[emailSettingsService.saveEmailSettings]", error);
    throw new Error("Could not save email settings.");
  }
}

// Fire a test email through the send-email function. supabase-js attaches the
// signed-in admin's JWT, which send-email verifies via is_platform_admin().
export async function sendTestEmail(to) {
  if (!EMAIL_RE.test(to || "")) throw new Error("Enter a valid email address.");
  const { data, error } = await supabase.functions.invoke("send-email", {
    body: { to, subject: "Brand Command test email", test: true, kind: "test" },
  });
  if (error) {
    // Surface the function's real reason (disabled, not configured, etc.).
    let msg = "Test send failed.";
    try {
      const ctx = await error.context?.json?.();
      if (ctx?.error) msg = ctx.error;
    } catch { /* ignore */ }
    console.error("[emailSettingsService.sendTestEmail]", error);
    throw new Error(msg);
  }
  if (!data?.success) throw new Error(data?.error || "Test send failed.");
  return data.data;
}
